// @todo: Migrate to 'Toast' from @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import {toast} from '@wandb/weave/common/components/elements/Toast';
import {captureAndLogError} from '@wandb/weave/common/util/sentry';
import {type ApolloQueryResult} from 'apollo-client/core/types';
import {useCallback, useEffect, useState} from 'react';

import config from '../../config';
import {
  type FetchPreviewableLinkQuery,
  type PreviewableLinkResourceType,
  useCreateAccessTokenMutation,
  useRevokeAccessTokenMutation,
} from '../../generated/graphql';
import {type ReportViewRef} from '../../state/reports/types';
import * as ViewActions from '../../state/views/actions';
import {useViewRefAction} from '../../state/views/hooks';
import {type ReportPageNormalized} from '../../state/views/report/types';
import {type LoadableView} from '../../state/views/types';
import {propagateErrorsContext} from '../../util/errors';
import {type ReportAuthor} from '../../util/gallery/shared';
import {isExportableAs} from '../../util/panels';
import {getPanelSpec, type LayedOutPanel} from '../../util/panelTypes';
import {editablePanelDOMID} from '../../util/report/export';
import {useReportProjects} from '../../util/report/useReportProjects';
import {generatePreviewableReportLink} from '../Reports/util';

type CreatePreviewFriendlyURLInput = {
  token?: string; // Access token for the report view
  userProvidedPreviewImageURL?: string; // User-provided preview image URL
  firstPanelExportableAsImage: LayedOutPanel | undefined; // The first panel in the report that can be exported as an image
  origin: string; // Report URL origin
  encodedPathname: string; // Report URL pathname
  view: LoadableView; // Report view
  reportAuthors: ReportAuthor[]; // Report authors
  isMounted: () => boolean; // Function to check if the component is mounted
  setDidAttemptCreatePFURLSinceMount?: (didCreatePFRUL: boolean) => void; // Function to set whether we've attempted to create a preview-friendly URL since the component mounted
  refetchPreviewFriendlyURL?: (
    variables?:
      | {
          resourceID: string;
          resourceType: PreviewableLinkResourceType;
        }
      | undefined
  ) => Promise<ApolloQueryResult<FetchPreviewableLinkQuery>>; // Function to refetch the preview-friendly URL
};

// This function is used to create a preview-friendly URL for the report if one
// does not already exist and the report has an exportable panel.
export const createPreviewFriendlyURL = async ({
  token,
  userProvidedPreviewImageURL,
  firstPanelExportableAsImage,
  origin,
  encodedPathname,
  view,
  reportAuthors,
  isMounted,
  setDidAttemptCreatePFURLSinceMount,
  refetchPreviewFriendlyURL,
}: CreatePreviewFriendlyURLInput) => {
  if (
    (firstPanelExportableAsImage == null && !userProvidedPreviewImageURL) ||
    view.id == null
  ) {
    return;
  }
  if (config.ENVIRONMENT_IS_PRIVATE) {
    console.info(
      'Private environment detected - skipping creation of preview-friendly URL.'
    );
    return;
  }
  let panelDomEl = null;
  if (!userProvidedPreviewImageURL && firstPanelExportableAsImage) {
    const panelDomID = editablePanelDOMID(firstPanelExportableAsImage);
    panelDomEl = document.getElementById(panelDomID);
    if (panelDomEl == null) {
      const msg = `Invalid panel: ${panelDomID} not found in the DOM. Cannot create a preview-friendly URL.`;
      captureAndLogError(msg, {fingerprint: ['createPreviewFriendlyURL']});
      return;
    }
  }
  const reportAuthorsString =
    reportAuthors
      ?.map(author => author?.name)
      .filter(name => name != null && name !== '')
      .join(', ') || 'Weights & Biases';
  const redirectURL = `${origin}${encodedPathname}${
    token == null ? `` : `?accessToken=${token}`
  }`;
  const prevFriendlyURL = await generatePreviewableReportLink({
    userProvidedPreviewImageURL,
    panelDomEl,
    entityName: view.project?.entityName ?? '',
    title: view.displayName,
    description: view.description,
    viewID: view.id,
    redirectURL,
    author: reportAuthorsString,
  });
  if (prevFriendlyURL && refetchPreviewFriendlyURL) {
    try {
      await refetchPreviewFriendlyURL();
    } catch (e) {
      if (isMounted()) {
        console.error(e);
      }
    }
    setDidAttemptCreatePFURLSinceMount?.(true);
  }
  return prevFriendlyURL;
};

type IsPreviewFriendlyURLOutdatedInput = Pick<
  UseMaybeSurreptitiouslyCreatePreviewFriendlyURLInput,
  | 'fetchPreviewFriendlyURLData'
  | 'viewLastUpdatedAt'
  | 'refetchPreviewFriendlyURL'
>;

/**
 * This function is used to check if the current preview-friendly URL is outdated.
 * Returns true if there does not yet exist a preview-friendly URL or if the
 * preview-friendly URL was created/updated before the report was last updated.
 *
 */
export const isPreviewFriendlyURLOutdated = async ({
  viewLastUpdatedAt,
  fetchPreviewFriendlyURLData,
  refetchPreviewFriendlyURL,
}: IsPreviewFriendlyURLOutdatedInput) => {
  // Only create a new PFURL if the view has been updated since the current PFURL was created/updated
  let pfURLLastUpdatedAt: Date | undefined;
  if (fetchPreviewFriendlyURLData == null) {
    // Await the refetch of the preview-friendly URL if it hasn't been fetched yet
    try {
      const queryRes = await refetchPreviewFriendlyURL?.();
      if (
        queryRes?.data?.previewableLink?.updatedAt ||
        queryRes?.data?.previewableLink?.createdAt
      ) {
        pfURLLastUpdatedAt = new Date(
          queryRes.data.previewableLink.updatedAt ??
            queryRes.data.previewableLink.createdAt
        );
      }
    } catch (e) {
      console.error(e);
    }
  } else {
    if (
      fetchPreviewFriendlyURLData.previewableLink?.updatedAt ||
      fetchPreviewFriendlyURLData.previewableLink?.createdAt
    ) {
      pfURLLastUpdatedAt = new Date(
        fetchPreviewFriendlyURLData.previewableLink.updatedAt ??
          fetchPreviewFriendlyURLData.previewableLink.createdAt
      );
    }
  }
  if (pfURLLastUpdatedAt != null) {
    // If the PFURL was created/updated after the report was last updated, we don't need to create a new one
    if (pfURLLastUpdatedAt > new Date(viewLastUpdatedAt)) {
      return false;
    }
  }
  return true;
};

export type UseMaybeSurreptitiouslyCreatePreviewFriendlyURLInput = Omit<
  CreatePreviewFriendlyURLInput,
  'token'
> & {
  // Generated types do not match manually-defined types - using "any" hack for now
  currentAccessToken?: string; // Current public access token for the report view
  shouldCreateAndRevokeAccessTokenIfNotExists?: boolean; // Whether to create and revoke a public access token if one does not already exist to be used in the redirect URL
  createMagicLink?: (
    shouldSkipPFURLCreation?: boolean
  ) => Promise<string | undefined>; // async function to create a magic link
  deactivateLink?: (token: string) => Promise<void>; // async function to deactivate a magic link
  setIsSurreptitiouslyCreatingAndRevokingToken?: (
    isSurreptitiouslyCreatingAndRevokingToken: boolean
  ) => void; // Function to set whether the surreptitious access token creation/revoking is in progress
  userIsMemberOfTeam: boolean; // Whether the current user is a member of the team
  viewLastUpdatedAt: string; // ISO date string of when the report was last updated
  fetchPreviewFriendlyURLData?: FetchPreviewableLinkQuery; // Data from the fetchPreviewFriendlyURL query
};

/** This function is used to pre-generate a preview-friendly URL for the report
 * if one does not already exist and the report has an exportable panel, tracking
 * that we've just generated the
 * URL so that we can skip this time-consuming step when the user toggles the magic
 * link on.
 */
export function useMaybeSurreptitiouslyCreatePreviewFriendlyURL({
  firstPanelExportableAsImage,
  origin,
  encodedPathname,
  view,
  reportAuthors,
  isMounted,
  setDidAttemptCreatePFURLSinceMount,
  currentAccessToken,
  refetchPreviewFriendlyURL,
  shouldCreateAndRevokeAccessTokenIfNotExists,
  createMagicLink,
  deactivateLink,
  setIsSurreptitiouslyCreatingAndRevokingToken,
  userIsMemberOfTeam,
  viewLastUpdatedAt,
  fetchPreviewFriendlyURLData,
}: UseMaybeSurreptitiouslyCreatePreviewFriendlyURLInput) {
  useEffect(() => {
    const maybeCreatePFURL = async () => {
      const userProvidedPreviewImageURL = view?.previewUrl;
      if (firstPanelExportableAsImage == null && !userProvidedPreviewImageURL) {
        return;
      }
      // Only create a new PFURL if the user is a member of the team
      if (!userIsMemberOfTeam) {
        return;
      }
      // Don't attempt to create PFURL in private environments
      if (config.ENVIRONMENT_IS_PRIVATE) {
        console.info(
          'Private environment detected - skipping creation of preview-friendly URL.'
        );
        return;
      }

      const isCurrentPFURLOutdated = await isPreviewFriendlyURLOutdated({
        viewLastUpdatedAt,
        fetchPreviewFriendlyURLData,
        refetchPreviewFriendlyURL,
      });

      // Only create a new PFURL if the view has been updated since the current PFURL was created/updated
      if (!isCurrentPFURLOutdated) {
        return;
      }

      let token = currentAccessToken;

      // This section is concerned w/ creating and revoking a new access token to be used in the redirect URL
      if (
        shouldCreateAndRevokeAccessTokenIfNotExists &&
        currentAccessToken == null
      ) {
        // Ensure caller has provided functions to create and revoke access tokens
        if (createMagicLink == null || deactivateLink == null) {
          console.error(
            'createMagicLink or deactivateLink is null, but shouldCreateAndRevokeAccessTokenIfNotExists is true and currentAccessToken is null'
          );
          return;
        }
        // Create a new access token to be used in the redirect URL (and revoke it after the PFURL is created)
        setIsSurreptitiouslyCreatingAndRevokingToken?.(true);
        try {
          const newToken = await createMagicLink(true);
          if (newToken == null) {
            console.error('createMagicLink returned null');
            setIsSurreptitiouslyCreatingAndRevokingToken?.(false);
            return;
          }
          token = newToken;
          // Revoke the access token we created
          try {
            await deactivateLink(token);
          } catch (e) {
            console.error('Error revoking access token:', e);
          }
          setIsSurreptitiouslyCreatingAndRevokingToken?.(false);
        } catch (e) {
          console.error(e);
          setIsSurreptitiouslyCreatingAndRevokingToken?.(false);
          return;
        }
      }

      try {
        await createPreviewFriendlyURL({
          userProvidedPreviewImageURL,
          token,
          firstPanelExportableAsImage,
          setDidAttemptCreatePFURLSinceMount,
          origin,
          encodedPathname,
          view,
          reportAuthors,
          isMounted,
          refetchPreviewFriendlyURL,
        });
      } catch (e) {
        console.error(e);
      }
    };

    maybeCreatePFURL();
    // We only want to run this effect once when the component mounts.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}

export const firstPanelExportableAsImageFromViewPart = (
  viewPart: ReportPageNormalized
) => {
  return viewPart.blocks
    .map(block => {
      const {metadata} = block;
      const {panels}: {panels: LayedOutPanel[]} =
        metadata?.panelBankSectionConfig ?? {panels: []};
      return panels.find(panel => {
        const panelSpec = getPanelSpec(panel.viewType);
        return isExportableAs(panelSpec, 'image');
      });
    })
    .filter(panel => panel != null)[0];
};

type useMagicLinkFunctionsInput = {
  // Generated types do not match manually-defined types - using "any" hack for now
  currentAccessToken?: string; // Current public access token for the report view
  isMounted: () => boolean; // Function to check if the component is mounted
  view: LoadableView; // Report view
  firstPanelExportableAsImage: LayedOutPanel | undefined; // The first panel in the report that can be exported as an image
  encodedPathname: string; // Report URL pathname
  setDidAttemptCreatePFURLSinceMount?: (didCreatePFRUL: boolean) => void; // Function to set whether we've attempted to create a preview-friendly URL since the component mounted
  refetchPreviewFriendlyURL?: (
    variables?:
      | {
          resourceID: string;
          resourceType: PreviewableLinkResourceType;
        }
      | undefined
  ) => Promise<ApolloQueryResult<FetchPreviewableLinkQuery>>; // Function to refetch the preview-friendly URL
  viewRef: ReportViewRef;
  didAttemptCreatePFURLSinceMount: boolean;
  viewPart: ReportPageNormalized;
  previewFriendlyURLData: FetchPreviewableLinkQuery | undefined;
  userProvidedPreviewImageURL: string | undefined;
};

// Returns 2 magic link functions necessary in share report trigger
export const useMagicLinkFunctions = ({
  view,
  currentAccessToken,
  viewRef,
  firstPanelExportableAsImage,
  refetchPreviewFriendlyURL,
  setDidAttemptCreatePFURLSinceMount,
  encodedPathname,
  isMounted,
  viewPart,
  previewFriendlyURLData,
  didAttemptCreatePFURLSinceMount,
  userProvidedPreviewImageURL,
}: useMagicLinkFunctionsInput) => {
  const [isCreatingAccessToken, setIsCreatingAccessToken] = useState(false);
  const [isRevokingAccessToken, setIsRevokingAccessToken] = useState(false);

  const addAccessToken = useViewRefAction(viewRef, ViewActions.addAccessToken);

  const removeAccessToken = useViewRefAction(
    viewRef,
    ViewActions.removeAccessToken
  );

  const projectSpecifiers = useReportProjects(viewRef);

  const [createAccessToken] = useCreateAccessTokenMutation({
    variables: {
      projects: projectSpecifiers,
      viewId: view.id!,
    },
    context: propagateErrorsContext(),
  });

  const [revokeAccessToken] = useRevokeAccessTokenMutation();

  const createMagicLink = useCallback(
    async (shouldSkipPFURLCreation: boolean = false) => {
      if (isCreatingAccessToken || currentAccessToken != null) {
        return currentAccessToken;
      }
      window.analytics?.track?.('Report View Only Link Created', {
        reportId: view.cid,
      });
      setIsCreatingAccessToken(true);
      const result = await createAccessToken();
      const accessToken = result.data?.createAccessToken?.accessToken;
      if (accessToken == null) {
        if (isMounted()) {
          toast('Failed to create access token -- please try again.', {
            type: 'error',
          });
          setIsCreatingAccessToken(false);
        }
        return;
      }
      addAccessToken(accessToken);
      setIsCreatingAccessToken(false);

      // The rest of this function is concerned with creating a preview-friendly URL
      if (didAttemptCreatePFURLSinceMount || shouldSkipPFURLCreation) {
        return accessToken.token;
      }
      if (config.ENVIRONMENT_IS_PRIVATE) {
        console.log(
          'Private environment detected; skipping creation of preview-friendly URL.'
        );
        return accessToken.token;
      }
      if (firstPanelExportableAsImage == null) {
        console.warn(
          "No panels are exportable as images; can't create preview-friendly url."
        );
        return accessToken.token;
      }

      // Only create a new PFURL if the view has been updated since the current PFURL was created/updated
      const isCurrentPFURLOutdated = await isPreviewFriendlyURLOutdated({
        viewLastUpdatedAt: view.updatedAt,
        fetchPreviewFriendlyURLData: previewFriendlyURLData,
        refetchPreviewFriendlyURL,
      });
      if (!isCurrentPFURLOutdated) {
        return accessToken.token;
      }
      await createPreviewFriendlyURL({
        token: accessToken.token,
        firstPanelExportableAsImage,
        setDidAttemptCreatePFURLSinceMount,
        origin,
        encodedPathname,
        view,
        reportAuthors: viewPart.authors ?? [],
        userProvidedPreviewImageURL,
        isMounted,
        refetchPreviewFriendlyURL,
      });
      return accessToken.token;
    },
    [
      isCreatingAccessToken,
      currentAccessToken,
      view,
      createAccessToken,
      addAccessToken,
      didAttemptCreatePFURLSinceMount,
      firstPanelExportableAsImage,
      encodedPathname,
      viewPart.authors,
      isMounted,
      refetchPreviewFriendlyURL,
      setDidAttemptCreatePFURLSinceMount,
      previewFriendlyURLData,
      userProvidedPreviewImageURL,
    ]
  );

  const deactivateLink = useCallback(
    async (inputToken?: string) => {
      const token = inputToken ?? currentAccessToken ?? '';
      if (isRevokingAccessToken || token === '') {
        console.warn(
          "Can't revoke access token; already revoking or no token."
        );
        return;
      }
      window.analytics?.track?.('Report Deactivate View Only Link Clicked', {
        reportId: view.cid,
      });
      setIsRevokingAccessToken(true);
      try {
        const result = await revokeAccessToken({
          variables: {
            token,
          },
        });
        if (result.data?.revokeAccessToken?.success) {
          removeAccessToken(token);
        }
      } catch (e) {
        console.error(e);
      }
      setIsRevokingAccessToken(false);
    },
    [
      isRevokingAccessToken,
      currentAccessToken,
      removeAccessToken,
      revokeAccessToken,
      view.cid,
    ]
  );
  return {
    deactivateLink,
    createMagicLink,
  };
};

// fetches the html from our previewable links and extracts the image url
export const getPreviewImageFromURL = async (
  currentPreviewFriendlyURLArg: string,
  setPreviewURL: (imageUrl: string) => void
) => {
  // eslint-disable-next-line wandb/no-unprefixed-urls
  const response = await fetch(currentPreviewFriendlyURLArg);
  switch (response.status) {
    case 200:
      try {
        const template = await response.text();
        const ogImageMetaTag = template
          .split('meta')
          .filter(tag => tag.includes('og:image'));
        const splitMetaTag = ogImageMetaTag[0].split('"');
        const imageUrl = splitMetaTag[splitMetaTag.length - 2];
        setPreviewURL(imageUrl);
      } catch (error) {
        console.error(error);
      }
      break;
    case 404:
      console.error('Preview image not found');
      break;
  }
};
